// Generated by tutorials/record.cjs from tutorials/articles/h25-discharge-readmit.cjs.
// Do not edit: change the script and run `node tutorials/record.cjs h25 --text-only` (wording) or
// `node tutorials/record.cjs h25` (pictures and clips).
import type { HelpArticle } from '../types';

const article: HelpArticle = {
  slug: 'discharge-readmit',
  catalogId: 'h25',
  title: 'Discharging and readmitting',
  summary: 'Discharge a resident so their trays stop, find them again later, and readmit them with everything you entered before, without typing it again.',
  audience: 'Dietary managers and anyone who keeps the census up to date',
  area: 'residents',
  learn: [
    'Find a resident on the Residents page',
    'Discharge a resident and take them off the tray line',
    'Find a discharged resident again',
    'Readmit a resident without retyping their record',
    'Check the diet order and tray line after a readmit',
  ],
  keywords: [
    'discharge',
    'discharged',
    'readmit',
    'readmission',
    're-admit',
    'hospital',
    'census',
    'return',
    'tray line',
    'leave',
  ],
  steps: [
    {
      id: 'find',
      part: 'Discharge',
      heading: 'Find the resident',
      text: 'In the menu, select Residents. Type part of the name in Search residents at the top, then select the resident in the list.',
      media: {
        image: {
          src: '/help/discharge-readmit/find.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/discharge-readmit/find.mp4',
          width: 1280,
          height: 748,
          durationSec: 5.5,
        },
        alt: 'The Residents page searched for Thornton, with Thornton, Gerald selected.',
      },
    },
    {
      id: 'discharge',
      part: 'Discharge',
      heading: 'Select Discharge',
      text: 'Select Discharge at the top of the list. Check the Date of Discharge, and leave Remove from tray line checked so the kitchen stops making the resident\'s trays.',
      media: {
        image: {
          src: '/help/discharge-readmit/discharge.webp',
          width: 708,
          height: 560,
        },
        clip: {
          src: '/help/discharge-readmit/discharge.mp4',
          width: 708,
          height: 560,
          durationSec: 2,
        },
        alt: 'The Discharge Resident window for Thornton, Gerald with today\'s date and Remove from tray line checked.',
      },
    },
    {
      id: 'discharged',
      part: 'Discharge',
      heading: 'Confirm',
      text: 'Select Discharge. The resident stays in the list as discharged, and the count of discharged residents at the bottom goes up by one.',
      media: {
        image: {
          src: '/help/discharge-readmit/discharged.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/discharge-readmit/discharged.mp4',
          width: 1280,
          height: 748,
          durationSec: 3,
        },
        alt: 'The Residents page with a message that Thornton, Gerald has been discharged, and the button above the list now reads Readmit.',
      },
    },
    {
      id: 'find-again',
      part: 'Readmit',
      heading: 'Find a discharged resident',
      text: 'When a resident comes back, search for them by name the same way. Select the resident, and the button above the list reads Readmit.',
      media: {
        image: {
          src: '/help/discharge-readmit/find-again.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/discharge-readmit/find-again.mp4',
          width: 1280,
          height: 748,
          durationSec: 5.5,
        },
        alt: 'The Residents page searched for Novak, with the discharged resident Novak, Stanley selected and the Readmit button.',
      },
    },
    {
      id: 'readmit',
      part: 'Readmit',
      heading: 'Select Readmit',
      text: 'Select Readmit. Check the Date of Readmit, then select Readmit in the window.',
      media: {
        image: {
          src: '/help/discharge-readmit/readmit.webp',
          width: 708,
          height: 404,
        },
        clip: {
          src: '/help/discharge-readmit/readmit.mp4',
          width: 708,
          height: 404,
          durationSec: 2,
        },
        alt: 'The Readmit Resident window for Novak, Stanley with today\'s date.',
      },
    },
    {
      id: 'readmitted',
      part: 'Readmit',
      heading: 'The resident is back',
      text: 'The resident is active again with everything entered before. They show No Tray Line, because readmitting clears the tray line.',
      media: {
        image: {
          src: '/help/discharge-readmit/readmitted.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/discharge-readmit/readmitted.mp4',
          width: 1280,
          height: 748,
          durationSec: 3.9,
        },
        alt: 'The Residents page with a message that Novak, Stanley has been readmitted, and No Tray Line in his Flags column.',
      },
    },
    {
      id: 'check',
      part: 'Readmit',
      heading: 'Check the diet order and tray line',
      text: 'Select Open. Check the diet order against the new orders. Then select Tray Line and Assign Tray Line to put the resident back on a cart, the same way as for a new admission.',
      media: {
        image: {
          src: '/help/discharge-readmit/check.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/discharge-readmit/check.mp4',
          width: 1280,
          height: 748,
          durationSec: 3.9,
        },
        alt: 'The resident\'s page with the diet order under the name, and the Diet Order, Assessment, Weight and Tray Line tiles, with Tray Line marked to show it needs attention.',
      },
    },
  ],
  tips: [
    'Use the date the resident actually left. You can pick an earlier date in the Discharge window.',
    'Discharged residents stay on the Residents page; the count at the bottom shows how many there are. Search by name to find one.',
  ],
  notes: [
    'Readmitting keeps the resident\'s record: diet order, allergies, meal pattern, adjustments and notes. Only the tray line is cleared, because the resident may come back to another room.',
    'A resident coming back from the hospital often has new orders. Check the diet order and consistencies before the first tray goes out.',
  ],
  related: ['admit-resident', 'change-diet-order', 'tray-line-setup'],
  recorded: '2026-10-25',
};

export default article;
